import { Icon } from "@studio3t/generic-components";
import { getIngestBaseUrl } from "@/constants";
import type { Service } from "@/types/api";
import { assertNever } from "@/utils/assertNever";

export interface ServiceUsage {
  name: string;
  running: boolean;
}

export function getProviderIcon(provider: Service["provider"]): Icon {
  switch (provider) {
    case "mongodb":
    case "postgres":
    case "postgres_cdc":
      return Icon.Database;
    case "pubsub":
      return Icon.Notification;
    case "kafka":
      return Icon.Lightning;
    case "udf":
      // A UDF service is its scripts folder (Figma 14058-10184).
      return Icon.Folder;
    case "http":
      return Icon.Link;
    case "http_source":
      return Icon.Connection;
    case "generator":
      return Icon.Lightning;
    case "scanner":
      return Icon.Database;
    case "anthropic":
    case "bedrock":
    case "openai":
      return Icon.Lightning;
    default:
      return assertNever(provider);
  }
}

/** "unknown" = the jobs query hasn't resolved, so live/idle can't be told yet. */
export type ServiceStatusValue = "live" | "idle" | "unknown";

// A service counts as live when at least one pipeline using it is currently
// running (the only live proof we have that its connection works); otherwise
// it's shown as idle. LLM services are the exception: pipelines never use
// them, so their live/idle comes from the backend's active-model flag — the
// agent always has exactly one LLM selected, and that one shows as live.
export function isLive(usage: ServiceUsage[]): boolean {
  return usage.some((u) => u.running);
}

// LLM rows resolve from the services response itself, so they are never
// "unknown"; other rows stay "unknown" until the jobs query resolves.
export function statusFor(
  service: Service,
  usage: ServiceUsage[],
  usageReady: boolean,
): ServiceStatusValue {
  if (isLlmService(service)) return service.is_active ? "live" : "idle";
  if (!usageReady) return "unknown";
  return isLive(usage) ? "live" : "idle";
}

export function maskConnectionString(connString?: string): string {
  if (!connString) return "—";

  try {
    const url = new URL(connString);
    if (url.password) {
      url.password = "****";
    }
    return url.host || `${connString.substring(0, 30)}...`;
  } catch {
    if (connString.length > 30) {
      return `${connString.substring(0, 30)}...`;
    }
    return connString;
  }
}

const HOST_RESOLVERS: Record<
  Service["provider"],
  (service: Service) => string
> = {
  mongodb: (s) => maskConnectionString(s.connection_string),
  postgres: (s) => maskConnectionString(s.connection_string),
  // Reads through the PostgreSQL service it targets; the slot is what
  // tells two captures of one database apart.
  postgres_cdc: (s) =>
    s.target_service ? `${s.target_service} · ${s.slot ?? "—"}` : "—",
  kafka: (s) => s["bootstrap.servers"] || "—",
  pubsub: (s) => s.project_id || "—",
  udf: () => "Local scripts",
  http: (s) => s.host || maskConnectionString(s.connection_string),
  http_source: (s) => `${getIngestBaseUrl(s.port)}/ingest`,
  // No host: it generates its own stream. Show the rate instead, which is
  // the only thing about a generator worth seeing in a list.
  generator: (s) =>
    Number.isFinite(s.events_per_second)
      ? `${s.events_per_second}/s`
      : "generated",
  // No host of its own: it reads through the service it targets, so that is
  // the only thing about it worth seeing in a list.
  scanner: (s) => s.target_service || "—",
  anthropic: (s) => s.model || "Anthropic API",
  bedrock: (s) => s.model_id || "AWS Bedrock",
  openai: (s) => s.model || "OpenAI API",
};

export function getHostPath(service: Service): string {
  return (HOST_RESOLVERS[service.provider] ?? (() => "—"))(service);
}

// LLM providers serve the AI agent, not pipelines: the backend keeps exactly
// one of them active (the agent's current model) and reports it via
// `is_active`, while `used_by_jobs` is always empty for them.
const LLM_PROVIDERS: ReadonlySet<Service["provider"]> = new Set([
  "anthropic",
  "bedrock",
  "openai",
]);

export function isLlmService(service: Service): boolean {
  return LLM_PROVIDERS.has(service.provider);
}

/**
 * Where each provider sits in the services list.
 *
 * A `Record` rather than an array because an array of provider names is not
 * checked for completeness: `scanner` was missing from the array this replaces,
 * and since the sort used `indexOf`, a scanner scored -1 and sorted above
 * everything. A Record makes a new provider a build error instead.
 */
const PROVIDER_RANK: Record<Service["provider"], number> = {
  mongodb: 0,
  postgres: 1,
  postgres_cdc: 2,
  pubsub: 3,
  kafka: 4,
  udf: 5,
  http: 6,
  http_source: 7,
  generator: 8,
  scanner: 9,
  anthropic: 10,
  bedrock: 11,
  openai: 12,
};

export function providerRank(provider: Service["provider"]): number {
  return PROVIDER_RANK[provider];
}

/** Every provider the app knows, in display order. */
export const SERVICE_PROVIDERS = Object.keys(
  PROVIDER_RANK,
) as Service["provider"][];

// Exhaustive on purpose: typed as `Record<string, string>` a new provider
// compiles fine and renders its raw name to the user. The compiler is the only
// thing that reliably notices.
export const PROVIDER_LABELS: Record<Service["provider"], string> = {
  mongodb: "MongoDB",
  postgres: "PostgreSQL",
  postgres_cdc: "PostgreSQL CDC",
  pubsub: "Google Pub/Sub",
  kafka: "Kafka",
  udf: "Scripts (UDF)",
  http: "HTTP Client",
  http_source: "HTTP Source",
  generator: "Generator",
  scanner: "Scanner",
  anthropic: "Anthropic",
  bedrock: "AWS Bedrock",
  openai: "OpenAI",
};
